import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageShell from '../components/site/PageShell';
import { images } from '../config/esanzaContent';
import { contentText, useWebsiteContent } from '../hooks/useWebsiteContent';
import { useBlogPosts } from '../hooks/useBlogPosts';

const Blog: React.FC = () => {
  const { t } = useTranslation();
  const { page } = useWebsiteContent('blog');
  const { posts, loading } = useBlogPosts();
  const [category, setCategory] = useState('All');
  const allLabel = t('site.blog.all', 'All');
  const headline = contentText(page, 'headline', page?.title || t('site.blog.title', 'Stories from the field.'));
  const subtext = page?.summary || t('site.blog.subtitle', 'Updates, field notes, and platform stories published by the eSanza team.');

  const categories = useMemo(() => {
    const values = posts.map((post) => post.category || 'Field Story').filter(Boolean);
    return ['All', ...Array.from(new Set(values))];
  }, [posts]);

  const filteredPosts = category === 'All'
    ? posts
    : posts.filter((post) => (post.category || 'Field Story') === category);

  return (
    <>
      <SEO
        title="Stories | eSanza"
        description={subtext}
        imagePath={images.tree}
        keywords="eSanza stories, digital agriculture, farm management, agricultural field intelligence, traceability"
      />
      <PageShell>
        <section className="relative overflow-hidden px-5 pb-16 pt-32 lg:px-8">
          <img src={images.tree} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,#06150e_0%,rgba(6,21,14,.9)_48%,rgba(6,21,14,.52)_100%)]" />
          <div className="relative mx-auto max-w-7xl">
            <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">{t('nav.blog', 'Stories')}</p>
            <h1 className="mt-5 max-w-4xl text-5xl font-black leading-[1] tracking-tight md:text-7xl">{headline}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-white/70">{subtext}</p>
          </div>
        </section>

        <section className="bg-[#f7f9f4] px-5 py-20 text-[#102016] lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex gap-2 overflow-x-auto pb-3">
              {categories.map((item) => (
                <button
                  key={item}
                  onClick={() => setCategory(item)}
                  className={`whitespace-nowrap rounded-full border px-5 py-2 text-sm font-extrabold transition ${category === item ? 'border-[#14532d] bg-[#14532d] text-white' : 'border-[#dbe5d2] bg-white text-[#506152] hover:border-[#14532d]'}`}
                >
                  {item === 'All' ? allLabel : item}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="mt-10 rounded-lg border border-[#dbe5d2] bg-white p-10 text-center text-sm font-semibold text-[#596959]">
                {t('site.blog.loading', 'Loading stories...')}
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="mt-10 rounded-lg border border-dashed border-[#cbd8c2] bg-white p-12 text-center">
                <h2 className="text-2xl font-black">{t('site.blog.empty_title', 'No published stories yet.')}</h2>
                <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-[#596959]">
                  {t('site.blog.empty_text', 'Stories will appear here after they are created and published from the website control dashboard.')}
                </p>
              </div>
            ) : (
              <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {filteredPosts.map((post) => (
                  <article key={post.slug} className="overflow-hidden rounded-lg border border-[#dbe5d2] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                    <Link to={`/blog/${post.slug}`} className="block">
                      <div className="aspect-[4/2.7] bg-[#102016]">
                        {post.image_url ? (
                          <img src={post.image_url} alt={post.title} className="h-full w-full object-cover" />
                        ) : (
                          <img src={images.traceability} alt="" className="h-full w-full object-cover opacity-80" />
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex flex-wrap gap-2">
                          <span className="rounded-full bg-[#edf4e7] px-3 py-1 text-xs font-extrabold text-[#14532d]">{post.category || t('site.blog.field_story', 'Field Story')}</span>
                          {post.featured && <span className="rounded-full bg-[#fff6cf] px-3 py-1 text-xs font-extrabold text-[#8a6b00]">{t('site.blog.featured', 'Featured')}</span>}
                        </div>
                        <h2 className="mt-4 text-xl font-black leading-tight">{post.title}</h2>
                        <p className="mt-3 line-clamp-3 text-sm leading-6 text-[#596959]">{post.excerpt || post.body}</p>
                        <p className="mt-5 text-xs font-bold uppercase tracking-[0.14em] text-[#987a1f]">{t('site.blog.read_story', 'Read story')}</p>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </PageShell>
    </>
  );
};

export default Blog;
